import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";

import { queryClient } from "App";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { getNextQueryParams } from "components/helpers";
import RuvixxDate from "components/RuvixxDate";
import useMetaSyncInProgress, {
  META_SYNC_POLL_INTERVAL_MS,
} from "hooks/useMetaSyncInProgress";
import MetaCampaignsTableActions from "routes/meta_leads/MetaCampaignsTableActions";
import MetaCampaignService from "services/MetaCampaign";

const campaignsQueryKey = accountId => ["meta-campaigns", accountId];

const CAMPAIGN_COLUMNS = [
  {
    header: "ID",
    id: "ID",
    accessorKey: "id",
    size: 50,
    cell: ({ row }) => (
      <p style={{ fontWeight: "bold" }}>{row.getValue("ID")}</p>
    ),
  },
  { id: "Name", accessorKey: "name", header: "Name", size: 220 },
  { id: "Objective", accessorKey: "objective", header: "Objective", size: 170 },
  { id: "Status", accessorKey: "status", header: "Status", size: 120 },
  {
    id: "Delivery",
    accessorKey: "effective_status",
    header: "Delivery",
    size: 140,
  },
  {
    id: "Daily Budget",
    accessorKey: "daily_budget",
    header: "Daily Budget",
    size: 130,
  },
  {
    id: "Start",
    accessorKey: "start_time",
    header: "Start",
    size: 160,
    cell: ({ row }) => {
      const date = row.getValue("Start");
      return date ? <RuvixxDate date={date} /> : null;
    },
  },
];

const MetaCampaignsTab = () => {
  const { accountId } = useParams();

  const isSyncing = useMetaSyncInProgress(accountId, "live");

  const invalidateCampaigns = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: campaignsQueryKey(accountId),
      }),
    [accountId]
  );

  const columns = useMemo(
    () => [
      ...CAMPAIGN_COLUMNS,
      {
        id: "Linked CRM Campaign",
        accessorKey: "crm_campaign_name",
        header: "Linked CRM Campaign",
        size: 220,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.crm_campaign_name ?? (
            <span className="tw:text-[var(--color-text-muted)]">
              Not linked
            </span>
          ),
      },
      {
        header: <div />,
        id: "Actions",
        accessorKey: "actions",
        size: 60,
        enableSorting: false,
        enableResizing: false,
        meta: { isDisplay: true },
        cell: ({ row }) => (
          <MetaCampaignsTableActions
            campaign={row.original}
            accountId={accountId}
            invalidate={invalidateCampaigns}
          />
        ),
      },
    ],
    [accountId, invalidateCampaigns]
  );

  const fetchCampaigns = useCallback(
    async queryParams => {
      const next = getNextQueryParams(queryParams, columns);
      return MetaCampaignService.list(accountId, next);
    },
    [accountId, columns]
  );

  return (
    <div className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:min-w-0">
      <BaseTableV3
        columns={columns}
        header="Campaigns"
        tableName="meta_campaigns"
        queryKey={campaignsQueryKey(accountId)}
        fetchData={fetchCampaigns}
        invalidateData={invalidateCampaigns}
        refetchInterval={isSyncing ? META_SYNC_POLL_INTERVAL_MS : undefined}
        enableSearch
        enableSettings
      />
    </div>
  );
};

export default MetaCampaignsTab;
